import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarClock, KeyRound, MapPin, Search } from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { DemoBanner, DemoCoachmark, DemoFooter } from "../components/DemoBanner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getDemoTokens, getDemoTourStep, setDemoTourStep } from "../lib/demoSession";

const fmtDate = (iso) => new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function DemoReferrals({ firstName = "Dr Alex Morgan", nav, linkMode = false, exitPath = "/clinicians", onExit, scope }) {
  const [tokens, setTokens] = useState(() => getDemoTokens(scope));
  const [selected, setSelected] = useState(null);
  const [tourStep, setTourStep] = useState(() => getDemoTourStep(scope));

  useEffect(() => {
    const refresh = (event) => {
      if (!event.detail || event.detail.scope === scope) setTokens(getDemoTokens(scope));
    };
    const refreshTour = (event) => {
      if (!event.detail || event.detail.scope === scope) setTourStep(getDemoTourStep(scope));
    };
    window.addEventListener("simplyrefer-demo-tokens", refresh);
    window.addEventListener("simplyrefer-demo-tour", refreshTour);
    return () => {
      window.removeEventListener("simplyrefer-demo-tokens", refresh);
      window.removeEventListener("simplyrefer-demo-tour", refreshTour);
    };
  }, [scope]);

  const finishTour = () => {
    setDemoTourStep(scope, 99);
    setTourStep(99);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={firstName} role="gp" sampleMode demoLinkMode={linkMode} demoNav={nav} demoScope={scope} />
      <DemoBanner kind="referrals" linkMode={linkMode} exitPath={exitPath} onExit={onExit} />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 animate-fadein" data-testid="demo-referrals-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Demo SimplyRefer References</h1>
            <p className="mt-1 text-sm text-slate-500">Fictional references created during this browser session, newest first.</p>
          </div>
          <span className="text-sm text-slate-500">{tokens.length} demo reference{tokens.length === 1 ? "" : "s"}</span>
        </div>

        {tourStep === 8 && tokens.length > 0 && (
          <DemoCoachmark title="Your demo reference is here" onNext={finishTour} nextLabel="Finish tour" className="mt-5">
            Open the reference to review its fictional details. In normal use, this page keeps SimplyRefer References easy to find without storing patient information.
          </DemoCoachmark>
        )}

        {tokens.length === 0 ? (
          <div className="mt-10 rounded-md border border-dashed border-slate-300 bg-white p-12 text-center">
            <KeyRound className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">No demo references yet</p>
            <p className="mt-1 text-sm text-slate-500">Choose a fictional clinician and create a demo SimplyRefer Reference.</p>
            <Button asChild className="mt-5"><Link to={nav.clinicians}><Search className="mr-2 h-4 w-4" /> Browse demo clinicians</Link></Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="demo-referrals-list">
            {tokens.map((token) => (
              <div key={token.id} className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-heading text-base font-semibold text-slate-900">{token.clinician.first_name} {token.clinician.last_name}</h2>
                      <span className="rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">DEMO ONLY</span>
                    </div>
                    <p className="text-sm text-slate-500">{token.clinician.profession} · {token.clinician.specialty}</p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {token.clinician.suburb}, {token.clinician.state}</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700"><KeyRound className="h-3 w-3" /> #{token.token}</span>
                    <p className="mt-1 flex items-center justify-end gap-1 text-xs text-slate-400"><CalendarClock className="h-3 w-3" /> {fmtDate(token.created_at)}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-slate-400" /> Referring clinic: <strong className="text-slate-800">{token.gp_clinic_name}</strong></span>
                  <Button size="sm" variant="outline" onClick={() => setSelected(token)} data-testid={"view-demo-token-" + token.id}>View demo reference</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DemoFooter linkMode={linkMode} exitPath={exitPath} onExit={onExit} />
      </main>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="demo-token-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Demo SimplyRefer Reference #{selected.token}</DialogTitle>
                <DialogDescription>This reference is fictional, session-only and has not been sent to a clinician.</DialogDescription>
              </DialogHeader>
              <div className="mt-3 space-y-3 text-sm">
                <Detail label="Clinician" value={selected.clinician.first_name + " " + selected.clinician.last_name} />
                <Detail label="Profession and specialty" value={selected.clinician.profession + " · " + selected.clinician.specialty} />
                <Detail label="Referring clinic" value={selected.gp_clinic_name} />
                <Detail label="Generated" value={fmtDate(selected.created_at)} />
                <pre className="whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">{selected.snippet}</pre>
                <p className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs font-medium text-blue-800">DEMO ONLY — Nothing has been saved to a live GP account or sent to this fictional clinician.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div><p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p><p className="mt-0.5 text-slate-800">{value}</p></div>
);
